import { Box, Button, Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import Checkbox from 'components/checkbox';
import Collapsible from 'components/collapsible';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { AlertCircle, Check, Info, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { postGenerationSync } from 'views/bluviu-nutrition/features/scenarios-v2/api/generations-get';
import DialogCageData from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/overview/dialog-cage-data';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { ScenarioCageData } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { UpdateScenarioOverviewInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-overview-input.type';
import {
  FishGroupValidationState,
  getFishGroupValidationState,
} from 'views/bluviu-nutrition/features/scenarios-v2/utils/fish-group-utils';

const SpinningRefreshCw = styled(RefreshCw, {
  shouldForwardProp: (prop) => prop !== 'isSpinning',
})<{ isSpinning: boolean }>(({ isSpinning }) => ({
  transformOrigin: 'center',
  transformBox: 'fill-box',
  animation: isSpinning ? 'spin 1s linear infinite' : 'none',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

const LoadLatestButton = styled(Button)(({ theme }) => ({
  padding: 0,
  '&:hover:not(:disabled)': { color: theme.palette.bluViu.o75 },
}));

const InvalidListRow = styled(ListRow, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError: boolean }>(({ hasError, theme }) =>
  hasError
    ? {
      '&& td, && td *': { color: theme.palette.error.main },
    }
    : {}
);

const getLatestFetchedAt = (cageData: ScenarioCageData[]): string => {
  const latest = cageData.reduce((max, cage) => {
    const fetchedAt = new Date(cage.fetchedAt);
    return fetchedAt > max ? fetchedAt : max;
  }, new Date(0));
  return latest.getTime() > 0 ? latest.toISOString() : '';
};

const FishDataLabel: React.FC = () => {
  const theme = useTheme();
  const {
    r2ScenarioQuery: { data: scenario, isFetching: isScenarioFetching },
  } = useR2ScenarioState();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const intl = useIntl();
  const params = useParams<R2ScenarioParams>();

  const [lastImported, setLastImported] = useState<string>('');

  const { mutate: syncGenerations, isPending: isLoading } = usePost<
    ScenarioCageData[],
    {
      scenarioId: string;
      globalSiteId: string;
    }
  >(
    ({ scenarioId, globalSiteId }) => postGenerationSync(scenarioId, globalSiteId),
    (data) => {
      setLastImported(getLatestFetchedAt(data));

      queryClient.invalidateQueries({
        queryKey: ['getR2Scenario', params.globalSiteId, params.scenarioId]
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  useEffect(() => {
    if (!scenario) return;
    setLastImported(getLatestFetchedAt(scenario.cageData));
  }, [scenario?.cageData]);

  const isSyncing = isLoading || isScenarioFetching;

  return (
    <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" flexWrap="wrap">
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <Check size={16} color={theme.palette.success.main} />
        <Stack>
          <Stack direction="row" gap={2} alignItems="center">
            <Typography>Fish data imported</Typography>
            <Divider variant="middle" orientation="vertical" flexItem />
            <LoadLatestButton
              variant="text"
              onClick={() => {
                if (scenario) {
                  syncGenerations({ scenarioId: scenario.id, globalSiteId: scenario.globalSiteId });
                }
              }}
              startIcon={<SpinningRefreshCw size={16} isSpinning={isSyncing} />}
              disabled={isSyncing}>
              Load latest data
            </LoadLatestButton>
          </Stack>
          {lastImported && (
            <Typography variant={'body2'} color={'text.secondary'}>
              Last imported: <FormattedDate value={lastImported} /> <FormattedTime value={lastImported} />
            </Typography>
          )}
        </Stack>
      </Stack>
      <DialogCageData />
    </Stack>
  );
};

const SectionFishDataContent: React.FC = () => {
  const theme = useTheme();
  const {
    r2ScenarioQuery: { data: scenario, isFetching },
  } = useR2ScenarioState();
  const { control } = useFormContext<UpdateScenarioOverviewInput>();
  const watchedCageData = useWatch({ control, name: 'cageData' });
  const watchedEndDate = useWatch({ control, name: 'endDate' });

  const { fields } = useFieldArray({
    control,
    name: 'cageData',
  });

  const getErrorMessages = (validationState: FishGroupValidationState): string[] =>
    [
      validationState.isStockingDateInvalid ? 'Stocking date must be on or before scenario start.' : '',
      validationState.isFishGroupStartDateInvalid ? 'Fish group start date is missing or invalid.' : '',
      validationState.isScenarioEndDateInvalid ? 'Scenario end date is missing or invalid.' : '',
      validationState.isScenarioDateRangeInvalid ? 'Scenario start must be before scenario end.' : '',
      validationState.isMeanWeightInvalid ? 'Weight must be between 50 and 3000 g.' : '',
    ].filter((message): message is string => !!message);

  const options: HeadCell[] = [
    {
      label: '',
      id: 'isIncluded',
      autoWidth: true,
      align: 'center',
    },
    { label: 'Name', id: 'fishGroupName' },
    { label: 'Stocking date', id: 'stockingDate' },
    { label: 'Scenario start', id: 'startDate' },
    { label: 'No. of fish', id: 'fishCount' },
    { label: 'Mean weight', id: 'meanWeight' },
  ];

  return (
    <Stack gap={2}>
      <Typography fontWeight={'bold'}>Fish groups</Typography>
      <List
        dark
        options={options}
        isLoading={isFetching}
        noResult={{
          show: !isFetching && !(scenario?.cageData ?? []).length,
          text: 'No cage data found',
        }}>
        {fields.map((field, index) => {
          const cage = scenario?.cageData[index];
          if (!cage) return null;
          const watchedCage = watchedCageData[index];
          const validationState = getFishGroupValidationState(
            {
              meanWeight: cage.meanWeight,
              stockingDate: cage.stockingDate,
              startDate: watchedCage?.startDate ?? cage.startDate,
            },
            watchedEndDate
          );
          const isInvalidIncludedCage = !!watchedCage?.isIncluded && validationState.isInvalid;
          const errorMessages = isInvalidIncludedCage ? getErrorMessages(validationState) : [];

          return (
            <InvalidListRow key={field.id} hasError={isInvalidIncludedCage}>
              <ListCell align="center">
                <Checkbox dark iconSize={28} hiddenLabel controller={{ name: `cageData.${index}.isIncluded`, control }} />
              </ListCell>
              <ListCell>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant={'body2'}>{cage.isVirtual ? 'Virtual Cage' : cage.fishGroupName}</Typography>
                  <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end">
                    {isInvalidIncludedCage && (
                      <Tooltip title={errorMessages.join(' ')}>
                        <Stack alignItems="center" color={theme.palette.error.main}>
                          <AlertCircle />
                        </Stack>
                      </Tooltip>
                    )}
                    {cage.isUserEdited && (
                      <Tooltip title="Fish group data edited from last import.">
                        <Stack alignItems="center">
                          <Info />
                        </Stack>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </ListCell>
              <ListCell>
                <FormattedDate value={cage.stockingDate} day={'numeric'} month={'short'} year={'numeric'} />
              </ListCell>
              <ListCell>
                <FormattedDate value={cage.startDate} day={'numeric'} month={'short'} year={'numeric'} />
              </ListCell>
              <ListCell>
                <FormattedNumber value={cage.fishCount} />
              </ListCell>
              <ListCell>
                <FormattedNumber value={cage.meanWeight} />
                {' g'}
              </ListCell>
            </InvalidListRow>
          );
        })}
      </List>
    </Stack>
  );
};

const SectionFishData: React.FC = () => {
  const theme = useTheme();
  const {
    r2ScenarioQuery: { data: scenario, isFetching },
  } = useR2ScenarioState();
  const { control } = useFormContext<UpdateScenarioOverviewInput>();
  const watchedCageData = useWatch({ control, name: 'cageData' });
  const watchedEndDate = useWatch({ control, name: 'endDate' });

  const hasInvalidIncludedCage = !!(
    !isFetching &&
    scenario?.cageData.some((cage, index) => {
      const watchedCage = watchedCageData[index];
      if (!watchedCage?.isIncluded) return false;

      return getFishGroupValidationState(
        {
          meanWeight: cage.meanWeight,
          stockingDate: cage.stockingDate,
          startDate: watchedCage.startDate,
        },
        watchedEndDate
      ).isInvalid;
    })
  );

  const hasInvalidFishGroupStartDate = !!(
    !isFetching && scenario?.cageData.some((cage, index) => {
      const watchedCage = watchedCageData[index];
      if (!watchedCage?.isIncluded) return false;

      return getFishGroupValidationState(
        {
          meanWeight: cage.meanWeight,
          stockingDate: cage.stockingDate,
          startDate: watchedCage.startDate,
        },
        watchedEndDate
      ).isFishGroupStartDateInvalid;
    })
  );

  const hasInvalidScenarioEndDate = !!(
    !isFetching &&
    scenario?.cageData.some((cage, index) => {
      const watchedCage = watchedCageData[index];
      if (!watchedCage?.isIncluded) return false;

      const validationState = getFishGroupValidationState(
        {
          meanWeight: cage.meanWeight,
          stockingDate: cage.stockingDate,
          startDate: watchedCage.startDate,
        },
        watchedEndDate
      );

      return validationState.isScenarioEndDateInvalid || validationState.isScenarioDateRangeInvalid;
    })
  );

  return (
    <Stack gap={2}>
      <Stack gap={1}>
        <Typography fontWeight={'600'}>Fish data *</Typography>
        <Typography color={'text.secondary'}>
          Import the latest data from site to run scenarios for each individual fish group.
        </Typography>
      </Stack>
      {hasInvalidIncludedCage && (
        <Stack direction="row" gap={2} alignItems="center" color={theme.palette.error.main}>
          <AlertCircle />
          <Box display={"flex"} flexDirection={"column"}>
            <Typography variant={'body2'}>
              <FormattedMessage id="bluViuNutritionR2.scenarioValidation.invalidFishGroupData" />
            </Typography>
            {hasInvalidFishGroupStartDate && (
              <Typography variant={'body2'}>
                <FormattedMessage id="bluViuNutritionR2.scenarioValidation.reviewFishGroupData" />
              </Typography>
            )}
            {hasInvalidScenarioEndDate && (
              <Typography variant={'body2'}>
                <FormattedMessage id="bluViuNutritionR2.scenarioValidation.reviewScenarioEndDate" />
              </Typography>
            )}
          </Box>

        </Stack>
      )}
      <Collapsible backgroundColor={theme.palette.blue.o25} hideBorder label={<FishDataLabel />}>
        <SectionFishDataContent />
      </Collapsible>
    </Stack>
  );
};

export default SectionFishData;
